import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../utils/api';
import { Megaphone, Save, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * AdminAdsTab — central controls for the Homepage-only Google AdSense integration.
 * Lets an admin set the publisher ID, per-device slot IDs, placement frequency and
 * enable/disable the whole integration (and each placement). Values are public
 * (they appear in client HTML / ads.txt), so this is purely a convenience console.
 */
const FIELDS = [
  { key: 'publisher_id', label: 'Publisher ID', placeholder: 'ca-pub-XXXXXXXXXXXXXXXX', hint: 'From AdSense → Account → Settings. Also used in ads.txt.' },
  { key: 'slot_mobile', label: 'Mobile ad slot ID', placeholder: 'e.g. 1234567890', hint: 'Shown after every N cards on phones/tablets (2-column grid).' },
  { key: 'slot_desktop_1', label: 'Desktop ad slot ID #1', placeholder: 'e.g. 1234567890', hint: 'First unit in the desktop ad row.' },
  { key: 'slot_desktop_2', label: 'Desktop ad slot ID #2 (optional)', placeholder: 'leave blank for a single desktop unit', hint: 'Second side-by-side desktop unit. Optional.' },
];

const AdminAdsTab = () => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getAdSenseConfig();
      setCfg(res.data);
    } catch (e) {
      toast.error('Failed to load AdSense config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: !!cfg.enabled,
        publisher_id: cfg.publisher_id || '',
        slot_mobile: cfg.slot_mobile || '',
        slot_desktop_1: cfg.slot_desktop_1 || '',
        slot_desktop_2: cfg.slot_desktop_2 || '',
        placement_mobile_enabled: !!cfg.placement_mobile_enabled,
        placement_desktop_enabled: !!cfg.placement_desktop_enabled,
        frequency_mobile: Number(cfg.frequency_mobile) || 4,
        frequency_desktop: Number(cfg.frequency_desktop) || 8,
      };
      const res = await adminAPI.updateAdSenseConfig(payload);
      setCfg(res.data);
      toast.success('AdSense settings saved');
    } catch (e) {
      toast.error('Failed to save AdSense settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !cfg) {
    return <div className="text-sm text-gray-500" data-testid="ads-tab-loading">Loading AdSense settings…</div>;
  }

  const slotsPending = !cfg.slot_mobile && !cfg.slot_desktop_1 && !cfg.slot_desktop_2;

  return (
    <div className="max-w-2xl" data-testid="admin-ads-tab">
      <div className="flex items-center gap-2 mb-1">
        <Megaphone size={18} className="text-[#1800ad]" />
        <h2 className="text-lg font-bold text-[#0A0B10]">Homepage Ads (Google AdSense)</h2>
      </div>
      <p className="text-xs text-[#6B7280] mb-5">Ads appear only on the Homepage — never on category, search, listing, profile, Post Ad, checkout, chat or admin pages.</p>

      {/* Master toggle */}
      <div className="flex items-center justify-between bg-[#f0edff] rounded-xl p-4 mb-5">
        <div>
          <p className="font-bold text-sm text-[#0A0B10]">AdSense enabled</p>
          <p className="text-xs text-[#6B7280]">Master switch for all Homepage ads.</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={!!cfg.enabled}
          onClick={() => set('enabled', !cfg.enabled)}
          data-testid="ads-enabled-toggle"
          className={`relative w-12 h-7 rounded-full transition-colors ${cfg.enabled ? 'bg-[#1800ad]' : 'bg-gray-300'}`}
        >
          <span className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${cfg.enabled ? 'translate-x-5' : ''}`} />
        </button>
      </div>

      {cfg.enabled && slotsPending && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5 text-amber-800 text-xs" data-testid="ads-slots-pending-warning">
          <AlertCircle size={15} className="shrink-0 mt-0.5" />
          <span>Ads are enabled but no slot IDs are set yet, so nothing will display. Add at least one slot ID below to start serving ads.</span>
        </div>
      )}

      {/* IDs */}
      <div className="space-y-4">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-xs font-bold text-[#4B5563]">{f.label}</label>
            <input
              type="text"
              value={cfg[f.key] || ''}
              onChange={(e) => set(f.key, e.target.value)}
              placeholder={f.placeholder}
              data-testid={`ads-input-${f.key}`}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 mt-1 text-sm outline-none focus:border-[#1800ad]"
            />
            <p className="text-[11px] text-[#9CA3AF] mt-1">{f.hint}</p>
          </div>
        ))}
      </div>

      {/* Placement toggles + frequency */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
        <div className="border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <p className="font-bold text-sm text-[#0A0B10]">Mobile / tablet</p>
            <input type="checkbox" checked={!!cfg.placement_mobile_enabled} onChange={(e) => set('placement_mobile_enabled', e.target.checked)} data-testid="ads-mobile-placement-toggle" className="w-4 h-4 accent-[#1800ad]" />
          </div>
          <label className="text-xs text-[#6B7280] mt-3 block">Show one ad after every
            <input type="number" min="1" value={cfg.frequency_mobile ?? 4} onChange={(e) => set('frequency_mobile', e.target.value)} data-testid="ads-freq-mobile" className="w-14 mx-1 rounded border border-gray-200 px-2 py-0.5 text-sm" />
            cards</label>
        </div>
        <div className="border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <p className="font-bold text-sm text-[#0A0B10]">Desktop / laptop</p>
            <input type="checkbox" checked={!!cfg.placement_desktop_enabled} onChange={(e) => set('placement_desktop_enabled', e.target.checked)} data-testid="ads-desktop-placement-toggle" className="w-4 h-4 accent-[#1800ad]" />
          </div>
          <label className="text-xs text-[#6B7280] mt-3 block">Show one ad row after every
            <input type="number" min="1" value={cfg.frequency_desktop ?? 8} onChange={(e) => set('frequency_desktop', e.target.value)} data-testid="ads-freq-desktop" className="w-14 mx-1 rounded border border-gray-200 px-2 py-0.5 text-sm" />
            cards</label>
        </div>
      </div>

      <div className="flex items-center gap-3 mt-6">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="ads-save-btn"
          className="inline-flex items-center gap-2 bg-[#1800ad] text-white rounded-full px-6 py-2.5 text-sm font-bold hover:bg-[#1400a0] disabled:opacity-60 transition-colors"
        >
          <Save size={15} /> {saving ? 'Saving…' : 'Save settings'}
        </button>
        {!slotsPending && cfg.enabled && (
          <span className="inline-flex items-center gap-1 text-xs text-green-600 font-bold"><CheckCircle2 size={14} /> Ready to serve</span>
        )}
      </div>

      <p className="text-[11px] text-[#9CA3AF] mt-5">ads.txt is served at <code>/ads.txt</code> with your publisher record. Google Auto Ads are not used. Each placement is clearly labelled "Advertisement".</p>
    </div>
  );
};

export default AdminAdsTab;
